import { useEffect, useRef, useState } from 'react';
import type { MindmapSpalte, SpaltenWert } from '@beholder/shared/module/mindmap';
import { datumDeutsch } from './format';
import { FARBTON } from './symbole';

export type Personen = Map<string, string>;

export function wertText(sp: MindmapSpalte, w: SpaltenWert | undefined, personen: Personen): string {
  if (w === null || w === undefined || w === '') return '';
  switch (sp.typ) {
    case 'betrag':
      return typeof w === 'number' ? w.toLocaleString('de-DE', { style: 'currency', currency: sp.waehrung || 'EUR' }) : String(w);
    case 'zahl':
      return typeof w === 'number' ? w.toLocaleString('de-DE', { maximumFractionDigits: 6 }) : String(w);
    case 'datum':
      return typeof w === 'string' ? datumDeutsch(w) : String(w);
    case 'person':
      return personen.get(String(w)) ?? 'unbekannt';
    case 'haken':
      return w ? 'ja' : '';
    case 'fortschritt':
      return typeof w === 'number' ? `${w.toLocaleString('de-DE', { maximumFractionDigits: 0 })} %` : String(w);
    default:
      return String(w);
  }
}

/** Anzeige eines Werts (Auswahl als farbige Marke, Haken als Zeichen). */
export function WertAnzeige({ sp, w, personen }: { sp: MindmapSpalte; w: SpaltenWert | undefined; personen: Personen }) {
  if (w === null || w === undefined || w === '') return null;
  if (sp.typ === 'auswahl') {
    const o = sp.optionen.find((x) => x.name === w);
    const f = o?.farbe ? FARBTON[o.farbe] : null;
    return (
      <span className="mm-option" style={f ? { background: `${f}26`, color: f, borderColor: `${f}66` } : undefined}>
        {String(w)}
      </span>
    );
  }
  if (sp.typ === 'haken') return w ? <span className="mm-haken" aria-label="ja">✓</span> : null;
  if (sp.typ === 'fortschritt' && typeof w === 'number') {
    const p = Math.max(0, Math.min(100, w));
    return (
      <span className="mm-fortschritt" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={p} aria-label={`${p} %`}>
        <span className="mm-fortschritt-balken">
          <span style={{ width: `${p}%` }} data-voll={p >= 100 ? '' : undefined} />
        </span>
        <span className="mm-fortschritt-zahl">{p} %</span>
      </span>
    );
  }
  // Link bewusst nur als Text: nie anklickbar, nie automatisch geöffnet oder abgerufen
  if (sp.typ === 'link') return <span className="mm-link" title={String(w)}>{String(w)}</span>;
  return <>{wertText(sp, w, personen)}</>;
}

/** Wert für das Eingabefeld (Zahlen mit Komma). */
function eingabeText(sp: MindmapSpalte, w: SpaltenWert | undefined): string {
  if (w === null || w === undefined) return '';
  if ((sp.typ === 'zahl' || sp.typ === 'betrag') && typeof w === 'number') return String(w).replace('.', ',');
  return String(w);
}

/**
 * Eingabe für einen Spaltenwert. Auswahl/Datum/Person/Haken übernehmen sofort,
 * Text und Zahl beim Verlassen oder mit Enter. Escape bricht ab.
 */
export function WertEingabe({
  sp,
  wert,
  personen,
  fertig,
  abbrechen,
  autoFocus,
  tasteWeiter,
}: {
  sp: MindmapSpalte;
  wert: SpaltenWert | undefined;
  personen: Personen;
  fertig(neu: SpaltenWert, weiter?: 'runter' | 'rechts' | 'links'): void;
  abbrechen?(): void;
  autoFocus?: boolean;
  /** Startwert beim Losschreiben in der Tabelle. */
  tasteWeiter?: string;
}) {
  const [text, setText] = useState(tasteWeiter ?? eingabeText(sp, wert));
  const erledigt = useRef(false);
  const fokus = useRef(false);
  useEffect(() => {
    if (!autoFocus && !fokus.current) setText(eingabeText(sp, wert));
  }, [wert, sp, autoFocus]);

  const senden = (neu: SpaltenWert, weiter?: 'runter' | 'rechts' | 'links') => {
    if (erledigt.current && autoFocus) return;
    if (autoFocus) erledigt.current = true;
    fertig(neu, weiter);
  };
  const tasten = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      erledigt.current = true;
      abbrechen?.();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      senden(text.trim() === '' ? null : text, 'runter');
    } else if (e.key === 'Tab') {
      e.preventDefault();
      senden(text.trim() === '' ? null : text, e.shiftKey ? 'links' : 'rechts');
    }
  };

  switch (sp.typ) {
    case 'auswahl':
      return (
        <select
          className="eingabe mm-wert-eingabe"
          autoFocus={autoFocus}
          value={typeof wert === 'string' ? wert : ''}
          onChange={(e) => senden(e.target.value || null)}
          onBlur={() => autoFocus && abbrechen?.()}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Tab' || e.key === 'Enter') {
              e.stopPropagation();
              e.preventDefault();
              if (e.key === 'Escape') abbrechen?.();
              else senden((e.target as HTMLSelectElement).value || null, e.key === 'Enter' ? 'runter' : e.shiftKey ? 'links' : 'rechts');
            }
          }}
        >
          <option value="">–</option>
          {sp.optionen.map((o) => (
            <option key={o.name} value={o.name}>
              {o.name}
            </option>
          ))}
        </select>
      );
    case 'person':
      return (
        <select
          className="eingabe mm-wert-eingabe"
          autoFocus={autoFocus}
          value={typeof wert === 'string' ? wert : ''}
          onChange={(e) => senden(e.target.value || null)}
          onBlur={() => autoFocus && abbrechen?.()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              abbrechen?.();
            }
          }}
        >
          <option value="">–</option>
          {[...personen].map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      );
    case 'haken':
      return (
        <input
          type="checkbox"
          className="mm-wert-haken"
          autoFocus={autoFocus}
          checked={wert === true}
          onChange={(e) => senden(e.target.checked)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              abbrechen?.();
            }
          }}
        />
      );
    case 'datum':
      return (
        <input
          type="date"
          className="eingabe mm-wert-eingabe"
          autoFocus={autoFocus}
          value={typeof wert === 'string' ? wert : ''}
          onChange={(e) => !autoFocus && senden(e.target.value || null)}
          onBlur={(e) => autoFocus && senden(e.target.value || null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape') {
              e.stopPropagation();
              e.preventDefault();
              const v = (e.target as HTMLInputElement).value || null;
              if (e.key === 'Escape') abbrechen?.();
              else senden(v, e.key === 'Enter' ? 'runter' : e.shiftKey ? 'links' : 'rechts');
            }
          }}
        />
      );
    default:
      return (
        <input
          className="eingabe mm-wert-eingabe"
          autoFocus={autoFocus}
          inputMode={sp.typ === 'zahl' || sp.typ === 'betrag' ? 'decimal' : sp.typ === 'fortschritt' ? 'numeric' : sp.typ === 'link' ? 'url' : undefined}
          spellCheck={sp.typ === 'link' ? false : undefined}
          autoComplete={sp.typ === 'link' ? 'off' : undefined}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => (fokus.current = true)}
          onBlur={() => {
            fokus.current = false;
            if (text !== eingabeText(sp, wert)) senden(text.trim() === '' ? null : text);
            else if (autoFocus) abbrechen?.();
          }}
          onKeyDown={tasten}
          placeholder={sp.typ === 'betrag' ? `0,00 ${sp.waehrung ?? 'EUR'}` : sp.typ === 'fortschritt' ? '0–100' : sp.typ === 'link' ? 'Adresse (nur als Text)' : ''}
        />
      );
  }
}
